import React, { useEffect, useState } from 'react';
import { Calendar, CheckCircle2, Clock, Download, LogIn, LogOut, RotateCcw, Search, Sparkles, Star, User, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { attendanceAPI, authAPI } from '../services/api';
import {
  ModernBadge,
  ModernButton,
  ModernCard,
  ModernLoadingSpinner,
  ModernModal,
  ModernSearchBar,
  ModernTable
} from '../components/ui/ModernComponents';

const Pointage = () => {
  const navigate = useNavigate();
  const { user, isEmployee, logout, refreshCheckInStatus } = useAuth();
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [todayMe, setTodayMe] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [filterEmployeeId, setFilterEmployeeId] = useState('all');
  const [filterStoreId, setFilterStoreId] = useState('all');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  const [filterPresenceStatus, setFilterPresenceStatus] = useState('all');
  const [filterProductivity, setFilterProductivity] = useState('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [stores, setStores] = useState([]);
  const [checkOutForm, setCheckOutForm] = useState({
    tasks_done: '',
    problems: '',
    productivity_stars: 3
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const meResPromise = attendanceAPI.getMyTodayAttendance();
      const allResPromise = isEmployee() ? Promise.resolve({ data: [] }) : attendanceAPI.getAttendance();
      const [allRes, meRes] = await Promise.all([allResPromise, meResPromise]);
      setAttendance(allRes.data || []);
      setTodayMe(meRes.data || null);

      if (!isEmployee()) {
        try {
          const storesRes = await authAPI.getStores();
          setStores(Array.isArray(storesRes.data) ? storesRes.data : []);
        } catch (storesError) {
          try {
            const myStoreRes = await authAPI.getMyStore();
            const myStore = myStoreRes?.data;
            if (myStore?.id) {
              setStores([myStore]);
            }
          } catch (myStoreError) {
            setStores([]);
          }
        }
      }
    } catch (error) {
      toast.error('Erreur lors du chargement du pointage');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatTime = (value) => {
    if (!value) return '-';
    return String(value).slice(0, 5);
  };

  const starsView = (count) => (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${star <= Number(count || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-neutral-300'}`}
        />
      ))}
    </div>
  );

  const stateMeta = (row) => {
    if (!row?.check_in) return { label: 'Sans check-in', variant: 'danger', rowClass: 'border-danger-100 bg-danger-50/40' };
    if (!row?.check_out) return { label: 'Sans check-out', variant: 'warning', rowClass: 'border-warning-100 bg-warning-50/40' };
    return { label: 'Journee complete', variant: 'success', rowClass: 'border-success-100 bg-success-50/40' };
  };

  const handleCheckIn = async () => {
    setSubmitLoading(true);
    try {
      await attendanceAPI.checkIn();
      toast.success('Check-in enregistre');
      setShowCheckInModal(false);
      await refreshCheckInStatus();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors du check-in');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCheckOut = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      await attendanceAPI.checkOut(checkOutForm);
      toast.success('Check-out enregistre');
      setShowCheckOutModal(false);
      setCheckOutForm({ tasks_done: '', problems: '', productivity_stars: 3 });
      await refreshCheckInStatus();
      logout();
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors du check-out');
    } finally {
      setSubmitLoading(false);
    }
  };

  const canCheckIn = !todayMe || !todayMe.check_in;
  const canCheckOut = !!todayMe?.check_in && !todayMe?.check_out;

  const managerAttendanceFiltered = attendance.filter((att) => {
    if (filterEmployeeId !== 'all' && String(att.employee_id) !== String(filterEmployeeId)) return false;
    if (filterStoreId !== 'all' && String(att.store_id) !== String(filterStoreId)) return false;
    if (filterFromDate && String(att.date || '').slice(0, 10) < filterFromDate) return false;
    if (filterToDate && String(att.date || '').slice(0, 10) > filterToDate) return false;

    if (filterPresenceStatus === 'missing_checkin' && !!att.check_in) return false;
    if (filterPresenceStatus === 'missing_checkout' && !!att.check_out) return false;
    if (filterPresenceStatus === 'complete_day' && (!att.check_in || !att.check_out)) return false;

    if (filterProductivity !== 'all') {
      const stars = Number(att.productivity_stars || 0);
      if (stars < Number(filterProductivity)) return false;
    }

    const text = searchTerm.toLowerCase();
    return (
      String(att.employee_name || '').toLowerCase().includes(text) ||
      String(att.date || '').toLowerCase().includes(text) ||
      String(att.tasks_done || '').toLowerCase().includes(text) ||
      String(att.problems || '').toLowerCase().includes(text)
    );
  });

  const filteredAttendance = managerAttendanceFiltered;

  const todayAttendance = attendance.filter((att) => {
    const dateValue = new Date(att.date);
    const now = new Date();
    return dateValue.toDateString() === now.toDateString();
  });

  const missingCheckInCount = attendance.filter((att) => !att.check_in).length;
  const missingCheckOutCount = attendance.filter((att) => !!att.check_in && !att.check_out).length;
  const fullDayCount = attendance.filter((att) => !!att.check_in && !!att.check_out).length;

  const tableColumns = [
    {
      key: 'attendance_state',
      label: 'Etat',
      render: (value, row) => {
        const state = stateMeta(row);
        return <ModernBadge variant={state.variant}>{state.label}</ModernBadge>;
      }
    },
    {
      key: 'employee_name',
      label: 'Employe',
      render: (value) => (
        <div className="flex items-center">
          <User className="w-4 h-4 text-neutral-500 mr-2" />
          <span className="font-semibold">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'date',
      label: 'Date',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-500 mr-2" />
          {formatDate(value)}
        </div>
      )
    },
    {
      key: 'check_in',
      label: 'Check-in',
      render: (value) => (
        <div className="flex items-center">
          <LogIn className="w-4 h-4 text-success-500 mr-2" />
          {formatTime(value)}
        </div>
      )
    },
    {
      key: 'check_out',
      label: 'Check-out',
      render: (value) => (
        <div className="flex items-center">
          <LogOut className="w-4 h-4 text-danger-500 mr-2" />
          {formatTime(value)}
        </div>
      )
    },
    { key: 'tasks_done', label: 'Taches', render: (value) => value || '-' },
    { key: 'problems', label: 'Problemes', render: (value) => value || '-' },
    { key: 'productivity_stars', label: 'Productivite', render: (value) => starsView(value) }
  ];

  const employeeOptions = Array.from(
    new Map(attendance.map((att) => [att.employee_id, { id: att.employee_id, name: att.employee_name }])).values()
  ).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

  const storeNameById = stores.reduce((acc, store) => {
    acc[String(store.id)] = store.name || `Magasin #${store.id}`;
    return acc;
  }, {});

  const storeOptions = Array.from(
    new Set(
      attendance
        .map((att) => att.store_id)
        .filter((value) => value !== null && value !== undefined)
    )
  )
    .map((storeId) => ({
      id: String(storeId),
      name: storeNameById[String(storeId)] || `Magasin #${storeId}`
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const resetManagerFilters = () => {
    setSearchTerm('');
    setFilterEmployeeId('all');
    setFilterStoreId('all');
    setFilterFromDate('');
    setFilterToDate('');
    setFilterPresenceStatus('all');
    setFilterProductivity('all');
  };

  const exportFilteredAttendanceCsv = () => {
    const rows = filteredAttendance;
    if (!rows.length) {
      toast.error('Aucune donnee a exporter');
      return;
    }

    const headers = [
      'Etat',
      'Employe',
      'Date',
      'Check-in',
      'Check-out',
      'Productivite',
      'Taches',
      'Problemes'
    ];

    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const stateLabel = (att) => {
      if (!att.check_in) return 'Sans check-in';
      if (!att.check_out) return 'Sans check-out';
      return 'Journee complete';
    };

    const csvBody = rows.map((att) => [
      stateLabel(att),
      att.employee_name || '',
      formatDate(att.date),
      formatTime(att.check_in),
      formatTime(att.check_out),
      att.productivity_stars || '',
      att.tasks_done || '',
      att.problems || ''
    ].map(escapeCsv).join(','));

    const csvContent = [headers.join(','), ...csvBody].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.setAttribute('download', `pointage-filtre-${stamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Export CSV termine');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 py-4 sm:py-6">
      <ModernCard className="relative overflow-hidden p-5 sm:p-7 bg-gradient-to-r from-slate-900 via-primary-900 to-indigo-900 border-0 text-white shadow-2xl">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -left-12 -bottom-12 w-44 h-44 rounded-full bg-primary-300/20 blur-2xl" />
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold bg-white/15 px-3 py-1 rounded-full mb-3">
              <Sparkles className="w-4 h-4" />
              Smart Attendance
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Pointage journalier</h1>
            <p className="text-sm sm:text-base text-primary-100 mt-1">
              {isEmployee()
                ? ''
                : 'Vue manager/admin moderne pour suivre les check-ins, check-outs et la productivite des equipes.'}
            </p>
          </div>
          {!isEmployee() && (
            <ModernSearchBar
              placeholder="Rechercher employe, date, tache ou probleme..."
              onSearch={setSearchTerm}
              className="w-full lg:w-96"
              debounce={300}
            />
          )}
        </div>
      </ModernCard>

      {isEmployee() ? (
        <ModernCard className="p-5 sm:p-7 border border-primary-100 bg-gradient-to-br from-white to-primary-50/30 shadow-xl">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h3 className="text-xl font-semibold text-neutral-900">Mes actions du jour</h3>
            <ModernBadge variant="primary">Workflow quotidien</ModernBadge>
          </div>
          <p className="text-sm text-neutral-600 mb-6">
            L'interface est optimisee mobile: une action claire le matin, une action claire a la fin de journee.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
            <div className="rounded-2xl border border-success-100 bg-success-50/70 p-3">
              <p className="text-xs text-success-700 font-semibold mb-2">Matin</p>
              <ModernButton
                variant="success"
                icon={<LogIn className="w-4 h-4" />}
                onClick={() => setShowCheckInModal(true)}
                disabled={!canCheckIn}
                className="w-full justify-center !rounded-xl !border-0 bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 text-white shadow-lg shadow-emerald-500/30 hover:brightness-105 hover:-translate-y-[1px] transition-all"
              >
                Check-in matin
              </ModernButton>
            </div>
            <div className="rounded-2xl border border-primary-100 bg-primary-50/70 p-3">
              <p className="text-xs text-primary-700 font-semibold mb-2">Fin de journee</p>
              <ModernButton
                variant="primary"
                icon={<LogOut className="w-4 h-4" />}
                onClick={() => setShowCheckOutModal(true)}
                disabled={!canCheckOut}
                className="w-full justify-center !rounded-xl !border-0 bg-gradient-to-r from-primary-600 via-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-500/30 hover:brightness-105 hover:-translate-y-[1px] transition-all"
              >
                Check-out fin de journee
              </ModernButton>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2 items-center">
            {canCheckIn && <ModernBadge variant="warning">Check-in en attente</ModernBadge>}
            {canCheckOut && <ModernBadge variant="primary">Pret pour check-out</ModernBadge>}
            {todayMe?.check_out && <ModernBadge variant="success">Journee complete</ModernBadge>}
          </div>
        </ModernCard>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            <ModernCard className="p-5 border border-neutral-100">
              <p className="text-sm text-neutral-500 mb-1">Pointages (total)</p>
              <p className="text-2xl font-bold text-neutral-900">{attendance.length}</p>
            </ModernCard>
            <ModernCard className="p-5 border border-primary-100 bg-primary-50/40">
              <p className="text-sm text-neutral-500 mb-1">Pointages aujourd'hui</p>
              <p className="text-2xl font-bold text-primary-700">{todayAttendance.length}</p>
            </ModernCard>
            <ModernCard className="p-5 border border-danger-100 bg-danger-50/50">
              <p className="text-sm text-neutral-500 mb-1">Sans check-in</p>
              <p className="text-2xl font-bold text-danger-600">{missingCheckInCount}</p>
            </ModernCard>
            <ModernCard className="p-5 border border-warning-100 bg-warning-50/50">
              <p className="text-sm text-neutral-500 mb-1">Sans check-out</p>
              <p className="text-2xl font-bold text-warning-600">{missingCheckOutCount}</p>
            </ModernCard>
            <ModernCard className="p-5 border border-success-100 bg-success-50/50">
              <p className="text-sm text-neutral-500 mb-1">Journees completes</p>
              <p className="text-2xl font-bold text-success-700">{fullDayCount}</p>
            </ModernCard>
          </div>

          <ModernCard className="p-5 sm:p-6 border border-neutral-100">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-neutral-900">Filtres avances</h3>
              <div className="flex items-center gap-2">
                <ModernButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAdvancedFilters((prev) => !prev)}
                >
                  {showAdvancedFilters ? 'Masquer' : 'Afficher'}
                </ModernButton>
                <ModernButton variant="secondary" size="sm" icon={<RotateCcw className="w-4 h-4" />} onClick={resetManagerFilters}>
                  Reinitialiser
                </ModernButton>
              </div>
            </div>
            {!showAdvancedFilters ? (
              <div className="rounded-xl border border-dashed border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-600">
                Filtres avances masques par defaut. Cliquez sur <span className="font-semibold">Afficher</span> pour ouvrir.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Employe</label>
                  <select
                    className="input w-full"
                    value={filterEmployeeId}
                    onChange={(e) => setFilterEmployeeId(e.target.value)}
                  >
                    <option value="all">Tous les employes</option>
                    {employeeOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name || `Employe #${option.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Magasin</label>
                  <select
                    className="input w-full"
                    value={filterStoreId}
                    onChange={(e) => setFilterStoreId(e.target.value)}
                  >
                    <option value="all">Tous les magasins</option>
                    {storeOptions.map((store) => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Presence</label>
                  <select
                    className="input w-full"
                    value={filterPresenceStatus}
                    onChange={(e) => setFilterPresenceStatus(e.target.value)}
                  >
                    <option value="all">Tous</option>
                    <option value="missing_checkin">Sans check-in</option>
                    <option value="missing_checkout">Sans check-out</option>
                    <option value="complete_day">Journee complete</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Du</label>
                  <input
                    type="date"
                    className="input w-full"
                    value={filterFromDate}
                    onChange={(e) => setFilterFromDate(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Au</label>
                  <input
                    type="date"
                    className="input w-full"
                    value={filterToDate}
                    onChange={(e) => setFilterToDate(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Productivite minimale</label>
                  <select
                    className="input w-full"
                    value={filterProductivity}
                    onChange={(e) => setFilterProductivity(e.target.value)}
                  >
                    <option value="all">Toutes</option>
                    <option value="1">1+ etoile</option>
                    <option value="2">2+ etoiles</option>
                    <option value="3">3+ etoiles</option>
                    <option value="4">4+ etoiles</option>
                    <option value="5">5 etoiles</option>
                  </select>
                </div>
              </div>
            )}
          </ModernCard>

          <ModernCard className="p-4 sm:p-6 border border-neutral-100">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-neutral-900">Suivi des employes</h3>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="flex items-center text-sm text-neutral-500">
                  <Search className="w-4 h-4 mr-1" />
                  {filteredAttendance.length} resultat(s)
                </div>
                <ModernButton
                  variant="secondary"
                  size="sm"
                  icon={<Download className="w-4 h-4" />}
                  onClick={exportFilteredAttendanceCsv}
                  className="!rounded-xl border-primary-200 hover:border-primary-300 hover:bg-primary-50 transition-all"
                >
                  Export CSV
                </ModernButton>
              </div>
            </div>
            <div className="sm:hidden space-y-3">
              {filteredAttendance.map((att) => {
                const state = stateMeta(att);
                return (
                  <ModernCard key={att.id} className={`p-4 border ${state.rowClass}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="font-semibold text-neutral-900">{att.employee_name || '-'}</p>
                        <p className="text-xs text-neutral-500">{formatDate(att.date)}</p>
                      </div>
                      <ModernBadge variant={state.variant}>{state.label}</ModernBadge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div className="rounded-lg bg-white/80 p-2 border border-neutral-100">
                        <p className="text-xs text-neutral-500">Check-in</p>
                        <p className="font-medium text-success-700">{formatTime(att.check_in)}</p>
                      </div>
                      <div className="rounded-lg bg-white/80 p-2 border border-neutral-100">
                        <p className="text-xs text-neutral-500">Check-out</p>
                        <p className="font-medium text-danger-700">{formatTime(att.check_out)}</p>
                      </div>
                    </div>
                    <div className="space-y-2 text-xs">
                      <div>
                        <p className="font-semibold text-neutral-600">Productivite</p>
                        {starsView(att.productivity_stars)}
                      </div>
                      <div>
                        <p className="font-semibold text-neutral-600">Taches</p>
                        <p className="text-neutral-700 line-clamp-2">{att.tasks_done || '-'}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-neutral-600">Problemes</p>
                        <p className="text-neutral-700 line-clamp-2">{att.problems || '-'}</p>
                      </div>
                    </div>
                  </ModernCard>
                );
              })}
              {filteredAttendance.length === 0 && (
                <ModernCard className="p-5 text-center border border-dashed border-neutral-200">
                  <p className="text-sm text-neutral-500">Aucun resultat pour les filtres selectionnes.</p>
                </ModernCard>
              )}
            </div>

            <div className="hidden sm:block overflow-x-auto rounded-xl border border-neutral-100">
              <ModernTable columns={tableColumns} data={filteredAttendance} className="w-full" />
            </div>
          </ModernCard>
        </>
      )}

      <ModernModal
        isOpen={showCheckInModal}
        onClose={() => setShowCheckInModal(false)}
        title="Check-in du matin"
        size="md"
      >
        <div className="space-y-4">
          <div className="rounded-xl bg-success-50 border border-success-100 p-4">
            <p className="text-sm text-success-800 font-medium mb-1">Pointage du matin</p>
            <p className="text-neutral-700">
              Enregistrer votre arrivee maintenant ?
            </p>
          </div>
          <p className="text-xs text-neutral-500">
            Cette action active l'acces a toutes les pages pour aujourd'hui.
          </p>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
            <ModernButton variant="secondary" onClick={() => setShowCheckInModal(false)} className="w-full sm:w-auto !rounded-xl hover:bg-neutral-100 transition-colors">
              Annuler
            </ModernButton>
            <ModernButton
              variant="success"
              icon={<CheckCircle2 className="w-4 h-4" />}
              onClick={handleCheckIn}
              disabled={submitLoading}
              className="w-full sm:w-auto !rounded-xl !border-0 bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/30 hover:brightness-105 transition-all"
            >
              {submitLoading ? 'Enregistrement...' : 'Confirmer check-in'}
            </ModernButton>
          </div>
        </div>
      </ModernModal>

      <ModernModal
        isOpen={showCheckOutModal}
        onClose={() => setShowCheckOutModal(false)}
        title="Check-out et bilan de la journee"
        size="lg"
      >
        <form onSubmit={handleCheckOut} className="space-y-5">
          <div className="rounded-xl bg-primary-50 border border-primary-100 p-4">
            <p className="text-sm text-primary-800 font-medium">Bilan obligatoire avant de quitter</p>
            <p className="text-xs text-neutral-600 mt-1">
              Une fois valide, vous serez deconnecte automatiquement.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Taches realisees</label>
            <textarea
              className="input min-h-28 w-full"
              value={checkOutForm.tasks_done}
              onChange={(e) => setCheckOutForm((prev) => ({ ...prev, tasks_done: e.target.value }))}
              placeholder="Decrivez les taches effectuees..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Problemes rencontres</label>
            <textarea
              className="input min-h-24 w-full"
              value={checkOutForm.problems}
              onChange={(e) => setCheckOutForm((prev) => ({ ...prev, problems: e.target.value }))}
              placeholder="Problemes ou blocages de la journee..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Productivite</label>
            <div className="flex flex-wrap items-center gap-2">
              {[1, 2, 3, 4, 5].map((starValue) => (
                <button
                  key={starValue}
                  type="button"
                  onClick={() => setCheckOutForm((prev) => ({ ...prev, productivity_stars: starValue }))}
                  className="p-1 rounded-md hover:bg-neutral-100 transition-colors"
                  aria-label={`Noter ${starValue} etoiles`}
                >
                  <Star
                    className={`w-7 h-7 ${
                      starValue <= Number(checkOutForm.productivity_stars)
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-neutral-300'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl bg-danger-50 border border-danger-100 p-3 flex items-start gap-2">
            <XCircle className="w-4 h-4 text-danger-500 mt-0.5" />
            <p className="text-xs text-danger-700">
              Apres validation du check-out, la session employe est fermee automatiquement.
            </p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-2">
            <ModernButton type="button" variant="secondary" onClick={() => setShowCheckOutModal(false)} className="w-full sm:w-auto !rounded-xl hover:bg-neutral-100 transition-colors">
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<LogOut className="w-4 h-4" />}
              disabled={submitLoading}
              className="w-full sm:w-auto !rounded-xl !border-0 bg-gradient-to-r from-primary-600 via-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-500/30 hover:brightness-105 transition-all"
            >
              {submitLoading ? 'Enregistrement...' : 'Valider check-out'}
            </ModernButton>
          </div>
        </form>
      </ModernModal>
    </div>
  );
};

export default Pointage;